import * as React from 'react';
import {FC} from 'react';
import {useField, useForm} from 'react-final-form';
import {isObject} from 'lodash';
import {apply as applyJsonLogic, RulesLogic} from 'json-logic-js';
import {ElementLogic} from './types';
import FormElement from './FormElement';
import FieldSet from './FieldSet';
import ListComponent from './ListComponent';
import { extractVarNames, replaceVarNames } from './jsonLogic';
import FormSpyFieldValues from './FormSpyFieldValues';

interface WithJsonLogicProps {
  parent: string,
  path: string,
  disabled?: boolean,
  visibilityRule?: RulesLogic,
}

const VisibilityContainer: FC<{visible?: boolean}> = ({visible, children}) => (
  <div className="visibility-container" style={{ display: visible ? 'block' : 'none' }}>
   {children}
  </div>
)

const WithJsonLogic: FC<WithJsonLogicProps> = React.memo(
  ({ visibilityRule, children, parent }) => {
    if (typeof visibilityRule === "undefined" || visibilityRule === null) {
      return (
        <VisibilityContainer visible={true}>{children}</VisibilityContainer>
      );
    }

    const fieldNames = extractVarNames(visibilityRule, parent);
    return (
      <FormSpyFieldValues fieldNames={fieldNames}>
        {values => {
          const newLogic = replaceVarNames(visibilityRule, values, parent);
          return (
            <VisibilityContainer visible={applyJsonLogic(newLogic)}>
              {children}
            </VisibilityContainer>
          );
        }}
      </FormSpyFieldValues>
    );
  },
  (prevProps, nextProps) =>
    prevProps.parent === nextProps.parent &&
    prevProps.path === nextProps.path &&
    isObject(prevProps.visibilityRule) === isObject(nextProps.visibilityRule)
);

const filterFormOptions = (options, parent) => {
  const cachedRelativeStates = [];
  const parentIndexes = parent.match(/\[[0-9]+\]/g);

  return options.filter(({ value, optionVisibilityRule }) => {
    if (typeof optionVisibilityRule === "undefined" || optionVisibilityRule === null) return true;

    const tmpRule = {...optionVisibilityRule};

    if (parentIndexes) {
      const matchInRule = parent.replace(/\[[0-9]+\]/g, '.*.');

      for (const ruleIndex of Object.keys(tmpRule)) {
        const rules = tmpRule[ruleIndex].map(function(ruleLine) {
          const {var: ruleVar = null} = {...ruleLine};

          if (ruleVar && ruleVar.includes(matchInRule)) {
            return { var: ruleVar.replace(matchInRule, parent) };
          }

          // In case the above doesn't match due to parent being a level too high
          if (ruleVar) {
            const matchInRuleParts = [];
            const matchInRuleReplacementParts = [];

            for (const [index, matchRulePart] of matchInRule.split('.*.').entries()) {
              if (ruleVar.includes(matchRulePart + '.*.') && parentIndexes[index]) {
                matchInRuleReplacementParts.push(
                  `${matchRulePart}${parentIndexes[index]}`
                );
                matchInRuleParts.push(
                  `${matchRulePart + '.*.'}`
                );
              }
            }

            if (matchInRuleParts && matchInRuleReplacementParts) {
              return {
                var: ruleVar.replace(
                  matchInRuleParts.join(''),
                  matchInRuleReplacementParts.join('')
                ),
              };
            }
          }

          return ruleLine;
        })

        tmpRule[ruleIndex] = rules;
      }
    }

    // Get relative data
    const relativeFields = extractVarNames(tmpRule, parent);
    const relativeFieldsIndex = relativeFields.join('_');

    if (!cachedRelativeStates[relativeFieldsIndex]) {
      cachedRelativeStates[relativeFieldsIndex] = relativeFields.reduce((state, field) => {
        const fieldToCheck = useField(field);
        const valueOfField = fieldToCheck?.input?.value;
        return { ...state, [field]: valueOfField };
      }, {});
    }

    const relativeState = cachedRelativeStates[relativeFieldsIndex];

    // Apply json-logic
    const relativeLogic = replaceVarNames(tmpRule, relativeState, parent);
    return applyJsonLogic(relativeLogic);
  });
}

interface ElementComponentProps {
  element: ElementLogic,
  parent: string,
  index?: number,
}

const createPath = (root: string, current: string): string => {
  if (root && current) return `${root}.${current}`;
  if (root) return root;
  return current;
}

const Element: FC<ElementComponentProps> = ({element, parent, index}) => {
  const form = useForm();
  const path = createPath(parent, element.name);
  const formPropOptions = element?.formProps?.options;
  const formProps = {
    ...element.formProps,
    ...(formPropOptions?.length > 0 && {
      options: filterFormOptions(formPropOptions, parent)
    })
  };

  if ((Array.isArray(element.children) && element.children.length && element.maxOccurs > 1)) {
    return (
      <FieldSet
        index={index}
        parent={path}
        type={element.formType}
        name={element.name}
        data={element.children}
        min={element.minOccurs}
        max={element.maxOccurs}
        formProps={formProps}
      />
    );
  }

  function resetObject<T = Record<string, any>>(value: T): T {
    const newObject = {} as T;

    Object.entries(value).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        newObject[key] = value.map(resetObject)
      } else {
        newObject[key] = key === 'id' ? value : null;
      }
    });

    return newObject;
  }

  return (
    <FormElement
      type={element.formType}
      props={{
        index: index,
        name: path,
        children: Array.isArray(element.children)
          ? <ListComponent data={element.children} parent={path} />
          : null,
        onDelete: element.formType === 'Container'
          ? () => form.change(path, resetObject(form.getFieldState(path)?.value ?? {}))
          : null,
        ...formProps,
      }}
    />
  );
}

const ElementComponent: FC<ElementComponentProps> = ({element, parent, index}) => {
  const path = createPath(parent, element.name);

  return (
    <WithJsonLogic parent={parent} path={path} visibilityRule={element.visibilityRule}>
      <Element
        element={element}
        parent={parent}
        index={index}
      />
    </WithJsonLogic>
  );
}

const MemoizedElementComponent = React.memo(
  ElementComponent,
  (prevProps, nextProps) => isObject(prevProps.element) === isObject(nextProps.element)
);

export default MemoizedElementComponent;
